import { NgZone } from '@angular/core';
import { EventDispatcher, Object3D, PerspectiveCamera, Plane, Raycaster, Vector2, Vector3 } from 'three';
import { Load } from './load';
import { PALLET_CONTENT_OVERLAY_NAME } from './loads-groups-drawer';
import RenderingContext from './rendering-context';
import { LoadingSpace } from './loading-space';
import { Unloading } from './unloading';
import { CookieService } from 'ngx-cookie-service';
import { Project } from '../../core/objects/project';
import { ProjectService } from '../../core/services/project.service';
import {
  cargoSpaceHasLoadedPalletLayers,
  getPalletLayerGroupIds,
} from '../../shared/helpers/pallet-loading-space.helper';

export interface LoadActionEvent {
  type: 'drag' | 'dragstart' | 'dragend' | 'contextmenu' | 'actions';
  object: Load;
  isBlocked?: boolean;
  mousePosition?: { x: number; y: number };
}

export class LoadActions extends EventDispatcher<{ drag: LoadActionEvent; dragstart: LoadActionEvent; dragend: LoadActionEvent; contextmenu: LoadActionEvent; actions: LoadActionEvent }> {
  private mouseDownEventReference: EventListener;
  private mouseUpEventReference: EventListener;
  private contextMenuEventReference: EventListener;
  private documentMouseUpEventReference: EventListener;

  private mousePosition = new Vector2();
  private raycaster: Raycaster;
  private selectedLoad: Load;
  private offset: Vector3;
  private plane: Plane;
  private intersectionWithPlane: Vector3;
  private readonly normalVector: Vector3 = new Vector3(0, -1, 0);
  private isActive: boolean;
  private isHintsEnabled = false;
  private canvasElement: HTMLElement;
  public isMultiSpace = false;
  private currentWorkSpaceIndex = 0;

  constructor(
    private loads: Load[],
    private renderingContext: RenderingContext<PerspectiveCamera>,
    private loadingSpace: LoadingSpace,
    private unloading: Unloading,
    private zone: NgZone,
    private cookieService: CookieService,
    private projectService: ProjectService,
  ) {
    super();
    this.canvasElement = this.renderingContext.renderer.domElement;
    this.raycaster = new Raycaster();
    this.offset = new Vector3();
    this.plane = new Plane();
    this.intersectionWithPlane = new Vector3();

    this.zone.runOutsideAngular(() => {
      this.activate();
    });
    this.canvasElement.style.cursor = 'grab';
    this.projectService.project.subscribe((project: Project) => {
      this.projectService.currentWorkSpaceIndex.subscribe((currentWorkSpace) => {
        this.currentWorkSpaceIndex = currentWorkSpace ?? 0;
        this.isMultiSpace = project.workSpaces[currentWorkSpace]?.stops
          ? project.workSpaces[currentWorkSpace].stops?.length > 0
          : false;
      });
    });
    const isVarioFloor =
      this.loadingSpace.varioTrailerFloorHeight || this.loadingSpace.varioSemiTrailerFloorHeight;
    if (isVarioFloor) {
      this.setStackedForVarioFloor();
    }
    const isBarrel = this.loads.find((load: Load) => load.loadType === 1);
    if (isBarrel) {
      this.setStackedForBarrels();
    }
  }

  private setStackedForBarrels(): void {
    this.loads.filter(load => load.loadType === 1).forEach((load1: Load) => {
      const isStacked = !!this.loads.find((load2: Load) => {
        return (
          load1.y + load1.height === load2.y &&
          load1.id !== load2.id &&
          load1.position.x <= load2.position.x &&
          load1.position.x + load1.width >= load2.position.x + load2.width &&
          load1.position.z <= load2.position.z &&
          load1.position.z + load1.length >= load2.position.z + load2.length
        );
      });
      load1.stacked = isStacked;
    });
  }

  private setStackedForVarioFloor(): void {
    const trailerFloorHeight = this.loadingSpace.varioTrailerFloorHeight;
    //const semiTrailerFloorHeight = this.loadingSpace.semiTrailerFloorHeight;
    this.loads.forEach((load) => {
      const isStacked = !!this.loads.find((load2) => {
        return (
          load.y2() < trailerFloorHeight &&
          load2.y < trailerFloorHeight &&
          load.y2() <= load2.y &&
          load.x < load2.x2() &&
          load.x2() > load2.x &&
          load.z < load2.z2() &&
          load.z2() > load2.z
        );
      });
      load.stacked = isStacked;
    });
    this.loads.forEach((load) => {
      const isStacked = !!this.loads.find((load2) => {
        return (
          load.y2() >= trailerFloorHeight &&
          load2.y >= trailerFloorHeight &&
          load.y2() <= load2.y &&
          load.x < load2.x2() &&
          load.x2() > load2.x &&
          load.z < load2.z2() &&
          load.z2() > load2.z
        );
      });
      if (load.y >= trailerFloorHeight) {
        load.stacked = isStacked;
      }
    });
  }

  public activate(): void {
    if (!this.isActive) {
      this.canvasElement.addEventListener('pointermove', this.onMouseMove, false);
      this.canvasElement.addEventListener(
        'pointerdown',
        (this.mouseDownEventReference = this.onMouseDown.bind(this) as EventListener),
        false,
      );
      this.canvasElement.addEventListener(
        'pointerup',
        (this.mouseUpEventReference = this.onMouseUp.bind(this) as EventListener),
        false,
      );
      this.canvasElement.addEventListener(
        'contextmenu',
        (this.contextMenuEventReference = this.onContextMenu.bind(this) as EventListener),
        false,
      );
      document.addEventListener(
        'pointerup',
        (this.documentMouseUpEventReference = this.onDocumentMouseUp.bind(this) as EventListener),
        false,
      );
      this.isActive = true;
    }
  }

  public deactivate(): void {
    if (this.isActive) {
      this.zone.runOutsideAngular(() => {
        this.canvasElement.removeEventListener('pointermove', this.onMouseMove, false);
        this.canvasElement.removeEventListener('pointerdown', this.mouseDownEventReference, false);
        this.canvasElement.removeEventListener('pointerup', this.mouseUpEventReference, false);
        this.canvasElement.removeEventListener(
          'contextmenu',
          this.contextMenuEventReference,
          false,
        );
        document.removeEventListener('pointerup', this.documentMouseUpEventReference, false);
      });
      this.isActive = false;
    }
  }

  public updateLoads(loads: Load[]): void {
    this.loads = loads;
    const isVarioFloor =
      this.loadingSpace.varioTrailerFloorHeight || this.loadingSpace.varioSemiTrailerFloorHeight;
    if (isVarioFloor) {
      this.setStackedForVarioFloor();
    }
    const isBarrel = this.loads.find((load: Load) => load.loadType === 1);
    if (isBarrel) {
      this.setStackedForBarrels();
    }
  }

  public updateLoadingSpace(loadingSpace: LoadingSpace): void {
    this.loadingSpace = loadingSpace;
  }

  private isOnLoadingSpace(
    loadingSpace: LoadingSpace,
    load: Load,
    cursorPosition: Vector3,
    isSet = false,
  ): boolean {
    const detectionSpaceSize = 5000;
    const loadingSpaceWidth = !isSet ? loadingSpace.width : loadingSpace.width1;
    const loadingSpaceLength = !isSet ? loadingSpace.length : loadingSpace.length1;
    const loadWidth = load.width / 2;
    const loadLength = load.length / 2;
    const loadXStart = cursorPosition.x;
    const loadXEnd = cursorPosition.x + loadWidth;
    const loadZStart = cursorPosition.z;
    const loadZEnd = cursorPosition.z + loadLength;

    const loadingSpaceStartPosition = !isSet ? 0 : 0 + loadingSpace.gap + loadingSpace.length;
    const loadingSpaceXStart = 0 - detectionSpaceSize;
    const loadingSpaceXEnd = loadingSpaceWidth + detectionSpaceSize;
    const loadingSpaceZStart = loadingSpaceStartPosition - detectionSpaceSize;
    const loadingSpaceZEnd = loadingSpaceStartPosition + loadingSpaceLength + detectionSpaceSize;

    const isInX = loadXEnd > loadingSpaceXStart && loadXStart < loadingSpaceXEnd;
    const isInZ = loadZEnd > loadingSpaceZStart && loadZStart < loadingSpaceZEnd;
    return isInX && isInZ;
  }

  private doesLoadFitDoor(load: Load, loadingSpace: LoadingSpace, isSet = false): boolean {
    const isTrailerDoorNull = !loadingSpace.trailerDoorWidth || !loadingSpace.trailerDoorHeight;
    const isSemiTrailerDoorNull =
      !loadingSpace.semiTrailerDoorWidth || !loadingSpace.semiTrailerDoorHeight;
    if (!isSet && isTrailerDoorNull) {
      return true;
    }
    if (isSet && isSemiTrailerDoorNull) {
      return true;
    }
    const doorWidth = !isSet ? loadingSpace.trailerDoorWidth : loadingSpace.semiTrailerDoorWidth;
    const doorHeight = !isSet ? loadingSpace.trailerDoorHeight : loadingSpace.semiTrailerDoorHeight;
    const loadWidth = load.width;
    const loadHeight = load.height;
    const loadLength = load.length;
    return (
      (loadWidth <= doorWidth && loadHeight <= doorHeight) ||
      (loadWidth <= doorHeight && loadHeight <= doorWidth) ||
      (loadLength <= doorWidth && loadHeight <= doorHeight) ||
      (loadWidth <= doorHeight && loadLength <= doorWidth) ||
      (loadLength <= doorHeight && loadHeight <= doorWidth)
    );
  }

  private isUnloaded(load: Load) {
    return (
      this.unloading?.position.x <= load.x &&
      this.unloading?.x2() >= load.x2() &&
      this.unloading?.position.z <= load.z &&
      this.unloading?.z2() >= load.z2()
    );
  }

  private setMousePosition(event: MouseEvent): void {
    const rect: ClientRect = this.canvasElement.getBoundingClientRect();
    this.mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private onMouseMove = (event: MouseEvent): void => {
    this.isHintsEnabled = this.cookieService?.get('hints').includes('true');
    event.preventDefault();
    this.setMousePosition(event);
    this.raycaster.setFromCamera(this.mousePosition, this.renderingContext.camera);
    this.raycaster.ray.intersectPlane(this.plane, this.intersectionWithPlane);
    const cursorPosition = this.intersectionWithPlane.sub(this.offset);
    let isBlocked = false;

    if (this.selectedLoad) {
      if (this.loadingSpace.type === 2) {
        const isSet = this.loadingSpace.set;
        const isOnLoadingSpace = this.isOnLoadingSpace(
          this.loadingSpace,
          this.selectedLoad,
          cursorPosition,
          false,
        );
        let isOnSemiLoadingSpace = false;
        if (isSet && !isOnLoadingSpace) {
          isOnSemiLoadingSpace = this.isOnLoadingSpace(
            this.loadingSpace,
            this.selectedLoad,
            cursorPosition,
            true,
          );
        }
        const doesLoadFitDoor = this.doesLoadFitDoor(
          this.selectedLoad,
          this.loadingSpace,
          isOnSemiLoadingSpace,
        );
        isBlocked = !doesLoadFitDoor && (isOnLoadingSpace || isOnSemiLoadingSpace);
      }
      if (!isBlocked) {
        const isPivot = this.selectedLoad.parent.parent.name.includes('container');
        if (isPivot) {
          this.selectedLoad.parent.parent.position.copy(cursorPosition);
        } else {
          this.selectedLoad.position.copy(cursorPosition);
        }
        this.dispatchEvent({ type: 'drag', object: this.selectedLoad, isBlocked });
      }

      if (this.isUnloaded(this.selectedLoad)) {
        return;
      }
      return;
    }

    const measureObject = this.renderingContext.scene.getObjectByName('Measure');
    const intersects = measureObject
      ? this.raycaster.intersectObjects([measureObject, ...this.loads], true)
      : this.raycaster.intersectObjects(this.loads, true);
    if (this.isHintsEnabled) {
      const hoveredLoad = this.resolveHoveredLoad(intersects[0]?.object);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const customEvent = new CustomEvent('loadHover', {
        detail: {
          load: hoveredLoad,
          mousePosition: { x: event.offsetX, y: event.offsetY },
        },
      });
      this.canvasElement.dispatchEvent(customEvent);
    }

    if (
      intersects.length > 0 &&
      !intersects[0]?.object?.parent?.name.includes('Measure') &&
      !(intersects[0].object as Load).stacked &&
      !this.isUnloaded(intersects[0].object as Load)
      // && !this.isMultiSpace
    ) {
      if (this.canvasElement.style.cursor !== 'grab') {
        this.canvasElement.style.cursor = 'grab';
      }
      this.plane.setFromNormalAndCoplanarPoint(this.normalVector, intersects[0].point);
    } else {
      if (this.canvasElement.style.cursor !== 'auto') {
        this.canvasElement.style.cursor = 'auto';
      }
    }
  };

  private onMouseDown(event: MouseEvent): void {
    // if (this.isMultiSpace) {
    //   return;
    // }
    event.preventDefault();
    this.setMousePosition(event);
    this.raycaster.setFromCamera(this.mousePosition, this.renderingContext.camera);
    this.raycaster.ray.intersectPlane(this.plane, this.intersectionWithPlane);

    const measureObject = this.renderingContext.scene.getObjectByName('Measure');
    const intersects = measureObject
      ? this.raycaster.intersectObjects([measureObject, ...this.loads], true)
      : this.raycaster.intersectObjects(this.loads, true);
    if (
      event.button === MouseButton.Left &&
      !intersects[0]?.object?.parent?.name.includes('Measure')
    ) {
      const dragTarget = intersects.length > 0 ? this.dragTargetFromHit(intersects[0].object) : undefined;
      if (intersects.length > 0) {
        this.canvasElement.dispatchEvent(
          new CustomEvent('colorchange', { detail: { load: dragTarget ?? intersects[0].object } }),
        );
      }
      if (
        dragTarget &&
        !dragTarget.stacked &&
        !this.isUnloaded(dragTarget) &&
        this.canDragLoad(dragTarget)
      ) {
        this.canvasElement.style.cursor = 'grabbing';
        // if ((intersects[0].object as Load).stackOnlyCurrentGroup) {
        //   this.selectedLoad = this.loads.find(
        //     (load) => load.idGroup === (intersects[0].object as Load).idGroup,
        //   );
        // } else {
        this.selectedLoad = dragTarget;
        // }
        this.offset.copy(this.intersectionWithPlane.sub(this.selectedLoad.position));
        this.dispatchEvent({ type: 'dragstart', object: this.selectedLoad });
        this.canvasElement.dispatchEvent(
          new CustomEvent('dragstart', { detail: { load: this.selectedLoad } }),
        );
      }
    }
  }

  private onMouseUp(event: MouseEvent): void {
    event.preventDefault();
    if (event.button === MouseButton.Left) {
      if (this.selectedLoad) {
        this.dispatchEvent({ type: 'dragend', object: this.selectedLoad });
        this.canvasElement.style.cursor = 'auto';
        this.mousePosition.set(0, 0);
        this.selectedLoad = null;
      }
    }
  }

  private dragTargetFromHit(object: Object3D): Load {
    let current: Object3D | undefined = object;
    while (current) {
      if (current.name === PALLET_CONTENT_OVERLAY_NAME) {
        let parent: Object3D | undefined = current.parent ?? undefined;
        while (parent) {
          if (parent instanceof Load && this.loads.includes(parent)) {
            return parent;
          }
          parent = parent.parent ?? undefined;
        }
        break;
      }
      current = current.parent ?? undefined;
    }
    return object as Load;
  }

  private canDragLoad(load: Load): boolean {
    if (this.loadingSpace.type !== 3) {
      return true;
    }
    const cargoSpace = this.getActiveCargoSpace();
    if (!cargoSpaceHasLoadedPalletLayers(cargoSpace)) {
      return true;
    }
    return !getPalletLayerGroupIds(cargoSpace).has(load.idGroup);
  }

  private canOpenContextMenu(load: Load): boolean {
    return this.canDragLoad(load);
  }

  private getActiveCargoSpace() {
    const project = this.projectService.getState();
    const stopIndex = this.projectService.getCurrentStopIndex();
    if (stopIndex !== undefined && stopIndex !== null && stopIndex >= 0) {
      return project.workSpaces[this.currentWorkSpaceIndex]?.stops?.[stopIndex]?.cargoSpaces?.[0];
    }
    return project.workSpaces[this.currentWorkSpaceIndex]?.cargoSpaces?.[0];
  }

  private onContextMenu(event: MouseEvent): void {
    if (event.button === MouseButton.Right) {
      const intersects = this.raycaster.intersectObjects(this.loads, true);
      if (intersects.length > 0) {
        const load = intersects[0].object as Load;
        if (!this.canOpenContextMenu(load)) {
          return;
        }
        load.unloaded = this.isUnloaded(load);
        this.dispatchEvent({
          type: 'actions',
          object: load,
          mousePosition: {
            x: event.offsetX,
            y: event.offsetY,
          },
        });
      }
    }
  }

  private onDocumentMouseUp(event: MouseEvent): void {
    event.preventDefault();
    if (event.button === MouseButton.Left) {
      if (this.selectedLoad) {
        this.canvasElement.style.cursor = 'initial';
        this.dispatchEvent({ type: 'dragend', object: this.selectedLoad });
        this.selectedLoad = null;
      }
    }
  }

  private resolveHoveredLoad(object: Object3D | undefined): Load | undefined {
    let current: Object3D | undefined = object;
    while (current) {
      if (current instanceof Load) {
        return current;
      }
      current = current.parent ?? undefined;
    }
    return undefined;
  }
}

export enum MouseButton {
  Left,
  Middle,
  Right,
}
